/**
 * @file modal.component.tsx
 * @description Базовый компонент модального окна для Task Manager приложения, который обеспечивает унифицированный интерфейс для отображения модальных диалогов с настраиваемым содержимым, заголовком и кнопками действий. Включает поддержку кнопки отмены, настраиваемые лейблы для кнопок подтверждения и отклонения, интеграцию с системой управления модальными окнами через useModalQueue, обработку событий подтверждения и отклонения с автоматическим закрытием окна.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-01-16
 * @dependencies ['react', '@consta/uikit/Modal', './components', '@hooks/use-modals', 'classnames', '@components/ui/base/button']
 * @related-files ['src/components/ui/layout/modal/components/header.component.tsx', 'src/hooks/use-modals.ts', 'src/components/ui/button']
 */

import type { FC, ReactNode } from 'react';
import { Modal as ConstaModal } from '@consta/uikit/Modal';
import { Header } from './components';
import { useModalQueue } from '@hooks/use-modals';
import cn from 'classnames';
import { Button } from '@components/ui/base/button';

/**
 * Props для компонента базового модального окна
 */
interface IModalProps {
    /** Содержимое модального окна */
    children: ReactNode;
    /** Видимость модального окна */
    isShow: boolean;
    /** Показывать ли кнопку "Отмена" */
    isWithCancel?: boolean;
    /** Функция для управления видимостью модального окна */
    setIsShow: (isShow: boolean) => void;
    /** Обработчик подтверждения действия */
    onConfirm?: () => void;
    /** Обработчик отклонения действия */
    onReject?: () => void;
    /** Заголовок модального окна */
    title: string;
    /** CSS класс для дополнительной стилизации */
    className?: string;
    /** Текст кнопки подтверждения */
    confirmLabel?: string;
    /** Текст кнопки отклонения */
    rejectLabel?: string | null;
    /** HTML контейнер для рендеринга модального окна */
    container?: HTMLDivElement;
}

export const Modal: FC<IModalProps> = ({
    children,
    setIsShow,
    isShow,
    title,
    className,
    container,
    isWithCancel = false,
    confirmLabel = 'Сохранить',
    rejectLabel = 'Не сохранять',
    onConfirm,
    onReject
}) => {
    const closeModalHandler = useModalQueue(setIsShow);

    return (
        <ConstaModal
            isOpen={isShow}
            hasOverlay={false}
             onClickOutside={(e: MouseEvent) => {
                if (e.target && !(e.target as Element).closest('.dhx_popup')) {
                    closeModalHandler();
                }
            }}
            position="center"
            onEsc={closeModalHandler}
            className={cn('modal grid w-[60.0179vmin]', className)}
            container={container}
        >
            <div className="modal_container flex flex-col h-full p-[3.1756vmin] ">
                <Header title={title} closeBtnClickHandler={() => closeModalHandler()} />

                {children}

                <div className="flex flex-row w-full justify-between items-end flex-1 h-full">
                    {isWithCancel && (
                        <Button
                            className="text-[1.482vmin]"
                            label="Отмена"
                            onClick={() => closeModalHandler()}
                            view="clear"
                        />
                    )}

                    <div>
                        <Button
                            className="mr-[0.8468vmin] text-[1.482vmin]"
                            label={confirmLabel}
                            onClick={() => {
                                onConfirm && onConfirm();
                                closeModalHandler();
                            }}
                            view="primary"
                        />

                        {rejectLabel && (
                            <Button
                                className="text-[1.482vmin]"
                                label={rejectLabel}
                                onClick={() => {
                                    onReject && onReject();
                                    closeModalHandler();
                                }}
                                view="ghost"
                            />
                        )}
                    </div>
                </div>
            </div>
        </ConstaModal>
    );
};